import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import Seal, { SEAL_META } from "@/components/Seal";
import { Upload, FileImage, Eye, Award, Building2, IdCard } from "lucide-react";
import { toast } from "sonner";

const SEAL_KINDS = [
  { id: "acis_accredited_member", label: "ACIS Accredited Member",  icon: Building2, badge: "ACIS · Institutional / Programmatic" },
  { id: "ibthm_board_certified",  label: "IBTHM Board Certified",   icon: Award,     badge: "IBTHM · BCHHP · BCT · BCDTS" },
  { id: "ibthm_licensed",         label: "IBTHM Licensed",          icon: IdCard,    badge: "IBTHM · LHT" },
];

export default function AdminSeals() {
  const [previewKind, setPreviewKind] = useState("ibthm_board_certified");
  const [stagedFile, setStagedFile] = useState({}); // {kind: File}

  const onPickFile = (kind, file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please pick an image file (PNG/SVG/JPG).");
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      toast.error("Image must be smaller than 4 MB.");
      return;
    }
    setStagedFile((s) => ({ ...s, [kind]: file }));
    toast.success(`${file.name} staged for ${kind}. Confirm upload below.`);
  };

  const confirmUpload = (kind) => {
    // The current production swap is done by replacing the file at
    // /app/frontend/public/seals/<filename>. Surfacing this clearly avoids
    // pretending an end-to-end upload pipeline exists when it doesn't.
    toast.info(
      "Upload workflow: send the staged file to the platform operator who will replace the seal asset and bust the CDN cache. A self-service upload pipeline is on the launch backlog.",
      { duration: 6000 },
    );
  };

  return (
    <div className="animate-fade-up" data-testid="admin-seals-page">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-2">Administration</div>
        <h1 className="font-heading text-3xl font-bold text-primary flex items-center gap-3">
          <FileImage className="h-7 w-7" /> Seal management
        </h1>
        <p className="text-sm text-slate-500 mt-1 max-w-2xl">
          The platform ships three official authority seals. Use this page to inspect the current
          artwork and preview how each seal renders on certificates and registry results before
          requesting a replacement upload.
        </p>
        <div className="mt-3 inline-flex items-center gap-2 text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded px-2.5 py-1" data-testid="seals-bulk-note">
          Note: Seal management is configuration, not record-keeping — bulk delete / purge does not apply here.
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-5 mb-8">
        {SEAL_KINDS.map((s) => {
          const Icon = s.icon;
          const meta = SEAL_META[s.id];
          const staged = stagedFile[s.id];
          return (
            <Card key={s.id} className="p-5 border-slate-200" data-testid={`seal-card-${s.id}`}>
              <div className="flex items-center gap-2 mb-3">
                <Icon className="h-4 w-4 text-primary" />
                <h2 className="font-heading text-sm font-bold text-primary">{s.label}</h2>
              </div>
              <Badge variant="outline" className="text-[10px] mb-4">{s.badge}</Badge>
              <div className="flex flex-col items-center bg-slate-50 border border-slate-200 rounded-md p-4 mb-3">
                <Seal kind={s.id} size="xl" />
                <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 mt-2">{meta.label}</span>
              </div>
              <div className="text-[11px] text-slate-500 font-mono-data truncate mb-2" title={meta.src}>{meta.src}</div>

              <div className="space-y-2">
                <Label className="text-xs">Replace seal (PNG / SVG, max 4 MB)</Label>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={(e) => onPickFile(s.id, e.target.files?.[0])}
                  data-testid={`seal-upload-${s.id}`}
                />
                {staged && (
                  <div className="text-[10px] text-emerald-700 truncate">
                    Staged: {staged.name} ({Math.round(staged.size / 1024)} KB)
                  </div>
                )}
                <div className="flex gap-2 pt-1">
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1"
                    onClick={() => setPreviewKind(s.id)}
                    data-testid={`seal-preview-${s.id}`}
                  >
                    <Eye className="h-3.5 w-3.5 mr-1" /> Preview
                  </Button>
                  <Button
                    size="sm"
                    disabled={!staged}
                    onClick={() => confirmUpload(s.id)}
                    className="bg-primary text-white hover:bg-[#113A64] flex-1"
                    data-testid={`seal-confirm-${s.id}`}
                  >
                    <Upload className="h-3.5 w-3.5 mr-1" /> Request upload
                  </Button>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      <Card className="p-6 border-slate-200" data-testid="seal-preview-card">
        <h2 className="font-heading text-lg font-bold text-primary mb-4">Placement preview</h2>
        <div className="grid lg:grid-cols-2 gap-6">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500 mb-2">Certificate footer</div>
            <div className="border-2 border-primary/30 bg-white p-6 rounded">
              <div className="text-center text-[10px] uppercase tracking-[0.22em] text-slate-500 mb-1">{SEAL_META[previewKind].authority}</div>
              <div className="text-center font-heading text-lg text-primary font-bold mb-3">Certificate Preview</div>
              <div className="flex items-end justify-between">
                <div className="text-[10px] text-slate-700">
                  <div className="uppercase tracking-[0.18em] text-[9px] font-bold text-slate-400">Credential</div>
                  <div className="font-mono-data">SAMPLE-2026-00001</div>
                </div>
                <Seal kind={previewKind} size="lg" />
              </div>
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500 mb-2">Registry result card</div>
            <Card className="p-4 border-slate-200">
              <div className="flex items-start gap-3">
                <Seal kind={previewKind} size="sm" />
                <div className="flex-1">
                  <div className="font-heading font-semibold text-primary text-base">Sample Holder</div>
                  <div className="text-xs text-slate-500 mt-0.5">Sample credential type</div>
                </div>
                <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">Active</Badge>
              </div>
            </Card>
          </div>
        </div>
      </Card>
    </div>
  );
}
